/**
 * Light / dark theme for the IronSight redesign (v2), frontend only and
 * remembered per browser (decision D4).
 *
 *   "system" (default) → no attribute; the prefers-color-scheme media query
 *                        in styles/variables.css decides
 *   "light" / "dark"   → data-theme="light|dark" on <html>, stored in
 *                        localStorage
 *
 * Only the new design has a dark palette. The dark token blocks are scoped
 * to `:root.ui-v2`, and the attribute is only ever set while <html> carries
 * the ui-v2 class (set by initUiVersion just before this runs), so the old
 * design (?ui=v1) stays light exactly as before. initUiVersion() applies the
 * theme before React mounts, so the first paint is already in the right
 * theme.
 *
 * Storage can be unavailable (private mode, blocked site data); every access
 * is guarded, and a choice that cannot be stored still holds for this page
 * load.
 */

import { useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'ironsight_theme';
export const THEME_CHOICES = ['light', 'dark', 'system'];
const THEME_EVENT = 'ironsight:themechange';
const DARK_QUERY = '(prefers-color-scheme: dark)';
const V2_CLASS = 'ui-v2';

const isChoice = (value) => THEME_CHOICES.includes(value);

// A choice made on this page load that storage would not take.
let memoryChoice = null;

/** The stored choice: "light", "dark" or (default) "system". */
export function readThemeChoice() {
    if (memoryChoice) return memoryChoice;
    let stored = null;
    try { stored = window.localStorage.getItem(THEME_STORAGE_KEY); } catch (e) { /* storage blocked */ }
    return stored === 'light' || stored === 'dark' ? stored : 'system';
}

const writeChoice = (choice) => {
    try {
        if (choice === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
        else window.localStorage.setItem(THEME_STORAGE_KEY, choice);
        return true;
    } catch (e) {
        return false;
    }
};

const prefersDark = () => {
    try { return Boolean(window.matchMedia && window.matchMedia(DARK_QUERY).matches); } catch (e) { return false; }
};

/** "light" or "dark": what a choice shows right now. */
export function resolveTheme(choice) {
    if (choice === 'light' || choice === 'dark') return choice;
    return prefersDark() ? 'dark' : 'light';
}

const v2Document = () => {
    try { return document.documentElement.classList.contains(V2_CLASS); } catch (e) { return false; }
};

/**
 * Put the choice on <html>. With the new design off (or no v2 flag given and
 * no ui-v2 class on <html>) the attribute is removed, never set.
 */
export function applyTheme(choice, v2On = v2Document()) {
    try {
        const root = document.documentElement;
        if (v2On && (choice === 'light' || choice === 'dark')) root.setAttribute('data-theme', choice);
        else root.removeAttribute('data-theme');
    } catch (e) { /* no document */ }
}

/** Apply the stored choice. Called by initUiVersion() before the app mounts. */
export function initTheme(v2On) {
    const choice = readThemeChoice();
    applyTheme(choice, v2On);
    return choice;
}

/** Remember and apply a choice, and tell every useTheme() about it. */
export function setThemeChoice(choice) {
    if (!isChoice(choice)) return;
    memoryChoice = writeChoice(choice) ? null : choice;
    applyTheme(choice);
    try { window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { choice } })); } catch (e) { /* no window */ }
}

/** The theme for components: { choice, resolved, setChoice }. */
export function useTheme() {
    const [choice, setChoiceState] = useState(readThemeChoice);
    const [systemDark, setSystemDark] = useState(prefersDark);

    useEffect(() => {
        const onChange = () => setChoiceState(readThemeChoice());
        // Another tab changed it: follow, so every open tab looks the same.
        const onStorage = (e) => {
            if (e.key !== THEME_STORAGE_KEY && e.key !== null) return;
            const next = readThemeChoice();
            applyTheme(next);
            setChoiceState(next);
        };
        window.addEventListener(THEME_EVENT, onChange);
        window.addEventListener('storage', onStorage);

        let mq = null;
        const onSystem = (e) => setSystemDark(Boolean(e.matches));
        try {
            mq = window.matchMedia ? window.matchMedia(DARK_QUERY) : null;
            if (mq && mq.addEventListener) mq.addEventListener('change', onSystem);
            else if (mq && mq.addListener) mq.addListener(onSystem);
        } catch (e) { mq = null; }

        return () => {
            window.removeEventListener(THEME_EVENT, onChange);
            window.removeEventListener('storage', onStorage);
            if (mq && mq.removeEventListener) mq.removeEventListener('change', onSystem);
            else if (mq && mq.removeListener) mq.removeListener(onSystem);
        };
    }, []);

    const resolved = choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;
    return { choice, resolved, setChoice: setThemeChoice };
}

/** Tests only: forget the in-memory fallback. */
export function __resetThemeForTests() {
    memoryChoice = null;
}
